var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { List } from "react-window";
import LeadsObjectApi from "../../../infrastructure/api/pages/lead-object-api";
import ObjectSearchApi from "../../../infrastructure/api/pages/object-search-api";
import CustomModal from "../modal/CustomModal";
const LISTBOX_ITEM_HEIGHT = 44;
const LISTBOX_MAX_VISIBLE = 8;
function VirtualRow({ index, style, items }) {
    return React.cloneElement(items[index], { style });
}
// The Lead dropdown backs onto every lead in the system (thousands of rows,
// no server-side paging) — MUI's Autocomplete renders every option as a real
// DOM node, so opening it without virtualization visibly stalls for a second
// or two. This windows the listbox so only the visible rows ever mount,
// regardless of how many leads exist. Not needed for Company Role (a couple
// dozen options at most).
//
// The outer <div> (not react-window's List itself) receives MUI's ref and
// event handlers — List's own ref exposes an imperative scroll API, not a
// plain DOM node, so it can't stand in for the ref MUI expects here.
const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(props, ref) {
    const { children } = props, other = __rest(props, ["children"]);
    const items = React.Children.toArray(children);
    const itemCount = items.length;
    const height = Math.min(itemCount, LISTBOX_MAX_VISIBLE) * LISTBOX_ITEM_HEIGHT;
    return (_jsx("div", Object.assign({ ref: ref }, other, { children: _jsx(List, { rowComponent: VirtualRow, rowCount: itemCount, rowHeight: LISTBOX_ITEM_HEIGHT, rowProps: { items }, overscanCount: 5, style: { height: height || LISTBOX_ITEM_HEIGHT } }) })));
});
// Write path deliberately does NOT reuse AssignObjectToLeadModal's own save call —
// that component posts straight to the legacy `lead-objects/assign-object-to-lead`
// endpoint, which carries no [Authorize] policy at all. This dialog instead calls
// the new, permission-gated `objects-search/link` endpoint (Permission.Objects.Create)
// so linking from this screen is actually enforced server-side. The lookup calls
// (leads dropdown, company roles) are safe to reuse as-is — they're read-only.
//
// Uses the app's shared CustomModal rather than a hand-rolled Dialog — every other
// modal in the app (ObjectModal, AssignObjectToLeadModal, etc.) goes through it, so
// reusing it is what keeps heading size/weight, button styling and spacing consistent
// with the rest of the app instead of drifting into a one-off look.
const LinkObjectDialog = ({ open, objectId, objectName, onClose, onLinked, }) => {
    const { t: translate } = useTranslation();
    const [leadOptions, setLeadOptions] = useState([]);
    const [companyRoles, setCompanyRoles] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!open || !objectId)
            return;
        setSelectedLead(null);
        setSelectedRole(null);
        setLoadingOptions(true);
        Promise.all([
            LeadsObjectApi.getAllLeadsExceptGivenObjectId(objectId),
            LeadsObjectApi.getCompanyRoles(),
        ])
            .then(([leadsRes, rolesRes]) => {
            var _a, _b;
            setLeadOptions((_a = leadsRes.data) !== null && _a !== void 0 ? _a : []);
            setCompanyRoles((_b = rolesRes.data) !== null && _b !== void 0 ? _b : []);
        })
            .catch(() => {
            toast.error(translate("Error while loading link options."));
        })
            .finally(() => setLoadingOptions(false));
    }, [open, objectId, translate]);
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!objectId || !selectedLead || !(selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.id))
            return;
        setSaving(true);
        try {
            yield ObjectSearchApi.link({
                objectId,
                leadId: selectedLead.value,
                companyRoleId: selectedRole.id,
            });
            toast.success(translate("Object linked successfully."));
            onLinked();
            onClose();
        }
        catch (error) {
            toast.error(((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) ||
                translate("Error while linking object to lead."));
        }
        finally {
            setSaving(false);
        }
    });
    return (_jsx(CustomModal, { open: open, onClose: () => {
            if (!saving)
                onClose();
        }, size: "xs", saving: saving, handleSaveButtonClick: handleSave, heading: translate("Link object to lead"), saveButtonText: translate("Link"), disabled: saving || !selectedLead || !selectedRole, children: _jsxs(Stack, { spacing: 2.5, sx: {
                pb: 1
            }, children: [objectName && (_jsx(Typography, { variant: "body2", sx: {
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                    }, children: objectName })), _jsx(Autocomplete, { options: leadOptions, getOptionLabel: (o) => o.text, loading: loadingOptions, value: selectedLead, onChange: (_, value) => setSelectedLead(value), isOptionEqualToValue: (o, v) => o.value === v.value, disableListWrap: true, 
                    // Each virtualized row has a fixed height — a long lead name
                    // wrapping to two lines would overflow that slot and overlap the
                    // row below it, so option text is forced to a single truncated line.
                    renderOption: (optionProps, option) => (_createElement(Box, Object.assign({ component: "li" }, optionProps, { key: option.value, sx: { minWidth: 0 } }),
                        _jsx(Typography, { noWrap: true, variant: "body2", children: option.text }))), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Lead"), fullWidth: true }))), slotProps: {
                        listbox: {
                            component: VirtualizedListbox
                        }
                    } }), _jsx(Autocomplete, { options: companyRoles, 
                    // CompanyRole.Name already includes its leading number (seeded as
                    // "{number} {name}", e.g. "2 Huurder") — prefixing o.number again
                    // here produced visibly duplicated numbers ("2 2 Huurder").
                    getOptionLabel: (o) => { var _a; return (_a = o.name) !== null && _a !== void 0 ? _a : ""; }, loading: loadingOptions, value: selectedRole, onChange: (_, value) => setSelectedRole(value), isOptionEqualToValue: (o, v) => o.id === v.id, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Company Role"), fullWidth: true }))) })] }) }));
};
export default LinkObjectDialog;
